import { useEffect, useRef } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { formatCurrency, restaurantPayout } from '../../shared/models';

// Fixed flashing banner pinned just below the header when new Pending orders
// arrive. Mirrors NewOrderBannerView.swift: red↔orange gradient pulse
// (.banner-flash), looping cash-register sound while visible, tap to open the
// order, X / ~10s auto-dismiss.
export default function NewOrderBanner({ orders = [], onOpen, onDismiss }) {
  const audioRef = useRef(null);
  const visible = orders.length > 0;

  useEffect(() => {
    if (!visible) return undefined;
    const audio = new Audio('/sounds/cash-register.mp3');
    audio.loop = true; // iOS: numberOfLoops = -1
    audio.volume = 1.0;
    audioRef.current = audio;
    audio.play().catch(() => {}); // autoplay may be blocked before first tap
    const timer = setTimeout(() => onDismiss?.(), 10000);
    return () => {
      clearTimeout(timer);
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
    };
  }, [visible]);

  if (!visible) return null;

  const first = orders[0];
  const total = orders.reduce((sum, o) => sum + restaurantPayout(o), 0);

  return (
    <div className="fixed top-[54px] inset-x-0 z-40 px-4 pointer-events-none">
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => e.key === 'Enter' && onOpen?.()}
        className="banner-flash pointer-events-auto max-w-lg lg:max-w-none mx-auto rounded-xl shadow-lg px-4 py-3.5 flex items-center gap-3 cursor-pointer select-none"
      >
        {/* Pulsing indicator (iOS flashing red/orange dot) */}
        <span className="relative flex w-3 h-3 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
        </span>

        <div className="flex-1 min-w-0">
          {orders.length === 1 ? (
            <>
              <p className="text-white font-bold text-sm leading-tight">🆕 New Order!</p>
              <p className="text-white/90 text-xs font-medium truncate mt-0.5">
                Order #{String(first.id).slice(0, 8)} • {first.customerName || 'Customer'}
              </p>
              <p className="text-white/80 text-[11px] mt-0.5">
                {formatCurrency(restaurantPayout(first))} • {first.orderItems.reduce((s, it) => s + (it.quantity || 1), 0)} items
              </p>
            </>
          ) : (
            <>
              <p className="text-white font-bold text-sm leading-tight">🆕 {orders.length} New Orders!</p>
              <p className="text-white/90 text-xs font-medium mt-0.5">Tap to acknowledge</p>
              <p className="text-white/80 text-[11px] mt-0.5">Total: XCD {formatCurrency(total)}</p>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label="Dismiss"
          onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
          className="shrink-0 w-7 h-7 rounded-full bg-white/20 text-white flex items-center justify-center active:bg-white/30"
        >
          <X size={15} />
        </button>
        <ChevronRight className="text-white/70 shrink-0" size={20} />
      </div>
    </div>
  );
}
